import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../context/AuthProvider';
import { useUser } from './useUser';
import { useAppConfig } from './useAppConfig';
import { GET_MANAGED_VIRTUAL_DIVISIONS } from '../apollo/queries/virtualHierarchy';
import {
    getRoleModules,
    getIsPriceSurveyModuleEnabled,
    getIsPriceChangeModuleEnabled,
    getIsTrainingModuleEnabled,
    MODULE_DATA,
    PRICE_SURVEY_ZONE_BYPASS_ROLES,
} from '../constants/navigation';

type ManagedVirtualSurveyStore = {
    isClosed: boolean;
    isPriceSurveyModuleEnabled: boolean;
    advertisingZone?: {
        isPriceSurveyModuleEnabled: boolean;
    } | null;
};

type ManagedVirtualDivisionsQuery = {
    getManagedVirtualDivisions: Array<{
        stores: ManagedVirtualSurveyStore[];
    }>;
};

export function useRoleModules() {
    const { role, authUser } = useAuth();
    const { isStoreManager } = useUser();
    const { trainingModuleEnabled } = useAppConfig();
    const { data: managedVirtualDivisions } =
        useQuery<ManagedVirtualDivisionsQuery>(GET_MANAGED_VIRTUAL_DIVISIONS, {
            skip: role !== 'divisionalmanager',
        });

    return useMemo(() => {
        const virtualSurveyStores =
            managedVirtualDivisions?.getManagedVirtualDivisions.flatMap(
                division => division.stores,
            ) ?? [];
        const modules = getRoleModules(role);
        const bypassZoneCheck = PRICE_SURVEY_ZONE_BYPASS_ROLES.includes(role);
        const filtered = modules.filter(moduleName => {
            if (moduleName === 'Price Surveys') {
                return (
                    bypassZoneCheck ||
                    getIsPriceSurveyModuleEnabled(
                        authUser,
                        role,
                        virtualSurveyStores,
                    )
                );
            }
            if (moduleName === 'Price Change') {
                return getIsPriceChangeModuleEnabled(authUser, isStoreManager);
            }
            if (moduleName === 'Training') {
                return getIsTrainingModuleEnabled(trainingModuleEnabled);
            }
            return true;
        });
        return filtered
            .map(moduleName => MODULE_DATA[moduleName])
            .filter(Boolean);
    }, [
        role,
        authUser,
        isStoreManager,
        trainingModuleEnabled,
        managedVirtualDivisions,
    ]);
}
